import {
  IMAGE_BANDS,
  ZOOM_SCALE_MAX,
  ZOOM_SCALE_MIN,
  ZOOM_SCALE_MIN_FACTOR,
} from './mapZoomRule'

// Expressions for a 9-patch fitted to its label AND riding the zoom ramp. Three things have to
// agree or the plate stops matching the text it wraps — the IMAGE baked per band, icon-size
// scaling the fitted quad to the live zoom, and the fit padding ramped one level ahead. Built
// together here so a change to one cannot miss the others.

// Stops sit outside the band they serve so no band shares one. As near half a level as it can
// go: below 0.5 each band still owns its pair, and the spare room is what a tile still on screen
// after the map zoomed past its band works with, since GL clamps at the pair and the box would
// otherwise freeze at the wrong size until the new tile lands.
const BOX_FIT_STOP_OFFSET = 0.45

// icon-text-fit sizes the label box from the text as SHAPED, and mapbox shapes a tile's text one
// zoom level AHEAD of the tile — text-size at floor(zoom) + 1 — while the glyphs render at
// text-size(zoom). On a ramped size the two disagree: the box comes out too roomy, the text sits
// against its top edge, and the oversized collision box evicts the circle. icon-size is the only
// property that scales a fitted quad, so that is where the factor is divided back out.
//
// GL narrows a size property's curve to the two stops bracketing [tileZoom, tileZoom + 1], so a
// stop on an integer would serve two bands with conflicting values. Offsetting gives every band
// its own pair, and the factor is linear in zoom inside a band, so the pair carries it exactly.
//
// The bracketing is per TILE, which is why text-size needs none of this: its stops span the whole
// ramp, while the box's pair is narrow by construction — see BOX_FIT_STOP_OFFSET.
export function getBoxedLabelFitStops(): [number, number][] {
  // Deliberately unclamped: both stops fall outside the band they serve, so they have to
  // continue its line rather than flatten onto the ramp's ends.
  const unclampedScale = (zoom: number): number =>
    ZOOM_SCALE_MIN_FACTOR +
    ((zoom - ZOOM_SCALE_MIN) / (ZOOM_SCALE_MAX - ZOOM_SCALE_MIN)) *
      (1 - ZOOM_SCALE_MIN_FACTOR)

  const stops: [number, number][] = []
  for (let band = ZOOM_SCALE_MIN - 1; band <= ZOOM_SCALE_MAX + 1; band++) {
    // Past either end of the ramp the text stops changing with zoom, so there is nothing to
    // divide out. The flat band above the ramp also pins every higher band's stops to 1.
    const isFlat = band + 1 <= ZOOM_SCALE_MIN || band >= ZOOM_SCALE_MAX
    const factorAt = (zoom: number): number =>
      isFlat ? 1 : unclampedScale(zoom) / unclampedScale(band + 1)

    const below = band - BOX_FIT_STOP_OFFSET
    const above = band + 1 + BOX_FIT_STOP_OFFSET
    stops.push([below, factorAt(below)], [above, factorAt(above)])
  }

  return stops.sort(([a], [b]) => a - b)
}

// icon-size for a fitted 9-patch: scale(zoom) / scale(tile + 1), which cancels the level mapbox
// shapes the text at and lands the quad on the label's own curve.
export const fittedImageSize = (isZoomScalingEnabled: boolean): unknown =>
  isZoomScalingEnabled
    ? ['interpolate', ['linear'], ['zoom'], ...getBoxedLabelFitStops().flat()]
    : 1

// Every band `bandedImage` can select, the floor band included, so a caller registering the
// rasters itself cannot warm a different set from the one the expression asks for.
//
// Warming all of them is a correctness fix, not an optimisation: `styleimagemissing` fires at
// most once per id per requestor, so a band whose tiles laid out while its raster was missing is
// never asked again and loses its art for the session.
export const bandedImageBands = (
  isZoomScalingEnabled: boolean,
): Array<number | null> =>
  isZoomScalingEnabled ? [ZOOM_SCALE_MIN - 1, ...IMAGE_BANDS] : [null]

// Picks the raster baked for the band this tile was laid out in: icon-image is layout, so the
// step is evaluated once per tile at the tile's own zoom. `idForBand` may return a whole
// expression rather than an id — a layer choosing between images per feature has to build that
// CHOICE inside each band, since zoom has to stay outermost.
export const bandedImage = (
  isZoomScalingEnabled: boolean,
  idForBand: (band: number | null) => unknown,
): unknown => {
  if (!isZoomScalingEnabled) return idForBand(null)
  const stops = IMAGE_BANDS.flatMap(band => [band, idForBand(band)])
  return ['step', ['zoom'], idForBand(ZOOM_SCALE_MIN - 1), ...stops]
}

// A px measurement that has to ride the label's curve, ramped ONE LEVEL AHEAD of the tile. This
// is a layout property, evaluated once per tile at the tile's own zoom, and GL then scales the
// fitted quad by icon-size — so ramping at the tile's own level renders it short by exactly the
// divisor. `at(factor)` builds the caller's own shape scaled by that factor: a step over label
// sizes for the location tier, a case over line counts for the pin tier.
export const rampedOneLevelAhead = (
  isZoomScalingEnabled: boolean,
  at: (factor: number) => unknown,
): unknown =>
  isZoomScalingEnabled
    ? [
        'interpolate',
        ['linear'],
        ['zoom'],
        ZOOM_SCALE_MIN - 1,
        at(ZOOM_SCALE_MIN_FACTOR),
        ZOOM_SCALE_MAX - 1,
        at(1),
      ]
    : at(1)
